import { useTracker } from '@archipro-website/tracker';
import type { ClickToRevealType } from '../component/click-to-reveal/ClickToReveal';
import { useSessionStorageState } from 'ahooks';

const DEFAULT_SUB_KEY = 'all';
export interface UseClickToRevealProps {
    revealType: ClickToRevealType;
    revealKey: string;
    revealSubKey?: string;
    /**
     * The professional whose contact details these are. A phone reveal is a lead,
     * so without this it is only counted site-wide and never towards that
     * client's advertising conversions.
     */
    professionalId?: number;
    /**
     * Persisted on `LoggedEvent.ExtraData` (JSON-encoded when not a string).
     * For Location reveals, pass `{ locationId, city?, suburb? }` so downstream
     * emails/analytics can show which showroom was opened.
     */
    extraData?: unknown;
}

export const useClickToReveal = ({
    revealType,
    revealKey,
    revealSubKey = DEFAULT_SUB_KEY,
    professionalId,
    extraData,
}: UseClickToRevealProps) => {
    const sessionKey = `reveal-${revealType}-${revealKey}`;
    // Several reveals share a key (one sub-key per location), so every
    // instance has to see the others' writes; otherwise the next reveal
    // writes back a stale list and drops the sub-keys revealed in between.
    const [revealedSubKeys, setRevealedSubKeys] = useSessionStorageState<
        string[]
    >(sessionKey, { defaultValue: [], listenStorageChange: true });
    const { log } = useTracker();

    const onRevealClick = () => {
        log(`${revealType}Reveal`, {
            url: new URL(window.location.href),
            professionalId,
            data: {
                ProfessionalID: professionalId,
                ...(extraData !== undefined ? { ExtraData: extraData } : {}),
            },
        });
        if (revealedSubKeys.includes(revealSubKey)) return;
        setRevealedSubKeys([...revealedSubKeys, revealSubKey]);
    };

    return {
        revealed:
            revealedSubKeys.includes(revealSubKey) ||
            revealedSubKeys.includes(DEFAULT_SUB_KEY),
        onRevealClick,
    };
};
